"use client";

import React, { useState } from "react";
import styled from "styled-components";
import Flex from "../../Flex";
import Text from "../../Text";
import Checkbox from "../../Checkbox";
import PopupMenu from "../../PopupMenu";
import { Badge } from "../../Badge";
import { getColor } from "../../utils";
import { BulkMultiSelectTrigger } from "./BulkCellContent";
import type { CellRegistration } from "./EditableTableContext";

const BulkCheckboxItem = styled(Flex).attrs({
  alignItems: "center",
  px: "s",
  height: "32px",
})`
  cursor: pointer;
  &:hover {
    background: ${getColor("grayLighter")};
  }
`;

type Props = {
  colIndex: number;
  options?: CellRegistration["options"];
  onCommit: (value: string) => void;
};

const BulkMultiSelectCell: React.FC<Props> = ({ colIndex, options, onCommit }) => {
  const [multiValues, setMultiValues] = useState<string[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuSource = `bulk-ms-${colIndex}`;
  return (
    <>
      <BulkMultiSelectTrigger
        type="button"
        data-popup-source={menuSource}
        onClick={() => setMenuOpen(o => !o)}>
        {multiValues.length > 0 ? (
          multiValues.map(v => {
            const opt = options?.find(o => o.value === v);
            return (
              <Badge
                key={v}
                text={opt?.label ?? v}
                background="grayLight"
                textColor="black"
                font="ui.small"
              />
            );
          })
        ) : (
          <Text style={{ fontSize: "inherit", color: "#aaa", fontStyle: "italic" }}>
            Bulk edit…
          </Text>
        )}
      </BulkMultiSelectTrigger>
      {menuOpen && (
        <PopupMenu source={menuSource} close={() => setMenuOpen(false)} stayOpenOnClick>
          {options?.map(opt => (
            <BulkCheckboxItem key={opt.value}>
              <Checkbox
                isSelected={multiValues.includes(opt.value)}
                label={opt.label}
                onClick={() => {
                  const next = multiValues.includes(opt.value)
                    ? multiValues.filter(v => v !== opt.value)
                    : [...multiValues, opt.value];
                  setMultiValues(next);
                  onCommit(next.join(","));
                }}
              />
            </BulkCheckboxItem>
          ))}
        </PopupMenu>
      )}
    </>
  );
};

export default BulkMultiSelectCell;
